import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import moment from "moment";
import { Drawer, Spin, Tag, Alert, Empty, Divider, message } from "antd";
import { ButtonItem, dineroDisabledMask } from "../../../../Components/Items";
import { COLORES_ESTADO } from "./Components/HabitacionCanvas";
import ModalTareaHabitacion, {
  PRIORIDADES,
  ESTATUS_TAREA,
} from "../AgendaHotel/ModalTareaHabitacion";

const formatearFecha = (fecha) => (fecha ? moment(fecha).format("DD/MM/YYYY") : "-");

//Bloque de una estancia. Se usa igual para la vigente, la anterior y la siguiente, así que el
//resumen de una habitación se lee siempre con el mismo formato.
const Estancia = ({ estancia, onVerVenta, onVerVentaProgramada, resaltar }) => (
  <div
    style={{
      border: `1px solid ${
        estancia.programada ? COLORES_ESTADO.programada.borde : resaltar ? "#00BFD6" : "#eef0f6"
      }`,
      background: estancia.programada
        ? COLORES_ESTADO.programada.fondo
        : resaltar
        ? "#00bfd60d"
        : "#fff",
      borderRadius: 8,
      padding: 10,
      marginBottom: 8,
    }}
  >
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
      <div style={{ fontWeight: 700 }}>{estancia.clienteNombre || "Cliente"}</div>
      {estancia.folio ? <Tag>{estancia.folio}</Tag> : null}
    </div>

    <div style={{ color: "#6b7280", fontSize: 12, marginTop: 4 }}>
      {formatearFecha(estancia.fechaLlegada)} → {formatearFecha(estancia.fechaSalida)}
      {estancia.noches ? ` · ${estancia.noches} noche(s)` : ""}
    </div>

    <div style={{ display: "flex", gap: 12, marginTop: 6, flexWrap: "wrap" }}>
      {estancia.totalRenglon !== undefined && estancia.totalRenglon !== null && (
        <span style={{ fontSize: 12 }}>
          Hospedaje: <strong>{dineroDisabledMask(estancia.totalRenglon)}</strong>
        </span>
      )}
      {estancia.saldo !== undefined && estancia.saldo !== null && (
        <span style={{ fontSize: 12 }}>
          Saldo de la venta:{" "}
          <strong style={{ color: estancia.saldo > 0 ? "#dc2626" : "#15803d" }}>
            {dineroDisabledMask(estancia.saldo)}
          </strong>
        </span>
      )}
    </div>

    <div style={{ marginTop: 6, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
      {estancia.programada && <Tag color="purple">Programada</Tag>}
      {estancia.entraHoy && <Tag color="blue">Entra hoy</Tag>}
      {estancia.saleHoy && <Tag color="orange">Sale hoy</Tag>}
      {estancia.esFactura ? <Tag color="purple">Facturada</Tag> : null}
      {/* Una reserva programada todavía no tiene venta que ver: el link va a la venta programada que
          la va a generar. */}
      {estancia.programada
        ? onVerVentaProgramada && (
            <ButtonItem
              cyanB
              size="small"
              onClick={() => onVerVentaProgramada(estancia.ventaProgramadaId)}
            >
              Ver venta programada
            </ButtonItem>
          )
        : onVerVenta && (
            <ButtonItem cyanB size="small" onClick={() => onVerVenta(estancia.ventaId)}>
              Ver venta
            </ButtonItem>
          )}
    </div>
  </div>
);

//Detalle de una habitación, abierto con doble clic en el lienzo (ver LienzoMapaHotel.js). Muestra la
//estancia de la fecha consultada más la anterior y la siguiente, para que recepción tenga el contexto
//sin salir del mapa. Molde: MapaWms/RackDetalleDrawer.js.
const HabitacionDetalleDrawer = ({
  visible,
  onClose,
  habitacionId,
  fecha,
  empresaId,
  headersToken,
  onVerVenta,
  onVerVentaProgramada,
}) => {
  const [cargando, setCargando] = useState(false);
  const [datos, setDatos] = useState(null);
  const [tareas, setTareas] = useState([]);
  const [modalTarea, setModalTarea] = useState({ visible: false, tarea: null });

  // Las tareas van en su propia llamada y no dentro de datosOcupacionHabitacion: se recargan solas al
  // crear o cerrar una, sin volver a calcular toda la ocupación de la habitación.
  const cargarTareas = useCallback(async () => {
    if (!habitacionId) return;
    try {
      const res = await axios.post(
        "hoteleria/selectTareasHabitacion",
        { empresaId, habitacionId },
        { headers: headersToken }
      );
      if (res.data.success === 2) setTareas(res.data.tareas || []);
    } catch (error) {
      console.error("Error al cargar las tareas de la habitación:", error);
    }
  }, [empresaId, headersToken, habitacionId]);

  const cargarDatos = useCallback(async () => {
    if (!habitacionId) return;
    setCargando(true);
    try {
      const res = await axios.post(
        "hoteleria/datosOcupacionHabitacion",
        { empresaId, habitacionId, fecha },
        { headers: headersToken }
      );
      if (res.data.success === 2) setDatos(res.data);
    } catch (error) {
      console.error("Error al cargar el detalle de la habitación:", error);
    } finally {
      setCargando(false);
    }
  }, [empresaId, headersToken, habitacionId, fecha]);

  useEffect(() => {
    if (visible) {
      cargarDatos();
      cargarTareas();
    } else {
      setDatos(null);
      setTareas([]);
    }
  }, [visible, cargarDatos, cargarTareas]);

  const cambiarEstatusTarea = (tarea, estatus) => {
    axios
      .post(
        "hoteleria/cambiarEstatusTareaHabitacion",
        { empresaId, tareaId: tarea._id, estatus },
        { headers: headersToken }
      )
      .then((res) => {
        if (res.data.success !== 2) {
          message.warning(res.data.message || "No se pudo cambiar el estatus");
          return;
        }
        cargarTareas();
      })
      .catch((error) => console.error("Error al cambiar el estatus de la tarea:", error));
  };

  const habitacion = datos?.habitacion;
  const vigente = datos?.vigente;
  const estanciasProgramadas = datos?.estanciasProgramadas || [];

  // La reserva programada que cubre la noche consultada, si la hay. El día de salida no cuenta: esa
  // noche el cuarto ya está libre.
  const programadaVigente = datos
    ? estanciasProgramadas.find(
        (valor) =>
          moment(valor.fechaLlegada) <= moment(datos.fecha) &&
          moment(valor.fechaSalida) > moment(datos.fecha)
      )
    : null;

  // Misma precedencia que el lienzo: la venta real manda sobre la programada, y las dos sobre "sin
  // tarifa".
  const estado = vigente
    ? vigente.estado
    : programadaVigente
    ? "programada"
    : habitacion && !habitacion.insumoId
    ? "sinTarifa"
    : "libre";
  const colores = COLORES_ESTADO[estado] || COLORES_ESTADO.libre;

  return (
    <Drawer
      title={habitacion ? `Habitación ${habitacion.numero}` : "Habitación"}
      visible={visible}
      onClose={onClose}
      width={420}
    >
      <Spin spinning={cargando}>
        {!datos ? (
          <Empty description="Sin datos" />
        ) : (
          <>
            <div style={{ marginBottom: 12 }}>
              <Tag color={colores.texto} style={{ fontWeight: 700 }}>
                {colores.etiqueta}
              </Tag>
              <span style={{ color: "#6b7280", fontSize: 12, marginLeft: 6 }}>
                al {formatearFecha(datos.fecha)}
              </span>
            </div>

            <div style={{ color: "#6b7280", fontSize: 12, marginBottom: 4 }}>
              {habitacion.nombre ? habitacion.nombre + " · " : ""}
              {habitacion.nivelNombre}
              {habitacion.capacidad ? ` · ${habitacion.capacidad} persona(s)` : ""}
            </div>

            <div style={{ fontSize: 12, marginBottom: 16 }}>
              Tarifa:{" "}
              {habitacion.insumoDescripcion ? (
                <strong>
                  {habitacion.insumoClave ? habitacion.insumoClave + " - " : ""}
                  {habitacion.insumoDescripcion}
                </strong>
              ) : (
                <span style={{ color: "#b45309" }}>sin insumo asignado</span>
              )}
            </div>

            {habitacion.observaciones ? (
              <Alert type="info" showIcon style={{ marginBottom: 16 }} message={habitacion.observaciones} />
            ) : null}

            {vigente && vigente.sobreventa && (
              <Alert
                type="error"
                showIcon
                style={{ marginBottom: 12 }}
                message="Sobreventa"
                description="Hay más de una estancia viva en esta fecha para la misma habitación. Revisa las ventas de abajo."
              />
            )}

            <Divider style={{ margin: "8px 0" }} orientation="left" plain>
              En esta fecha
            </Divider>
            {vigente && (vigente.estancias || []).length > 0 ? (
              (vigente.estancias || []).map((valorEstancia, indice) => (
                // La llave lleva el índice porque una misma venta puede traer dos renglones de la
                // misma habitación (justo el caso de sobreventa que hay que poder ver).
                <Estancia
                  key={`${valorEstancia.ventaId}-${indice}`}
                  estancia={valorEstancia}
                  onVerVenta={onVerVenta}
                  resaltar
                />
              ))
            ) : programadaVigente ? (
              <Estancia
                estancia={programadaVigente}
                onVerVentaProgramada={onVerVentaProgramada}
                resaltar
              />
            ) : (
              <div style={{ color: "#6b7280", fontSize: 12, marginBottom: 12 }}>
                La habitación está libre esta noche.
              </div>
            )}

            <Divider style={{ margin: "8px 0" }} orientation="left" plain>
              Estancia anterior
            </Divider>
            {datos.anterior ? (
              <Estancia estancia={datos.anterior} onVerVenta={onVerVenta} />
            ) : (
              <div style={{ color: "#6b7280", fontSize: 12, marginBottom: 12 }}>Sin estancias previas.</div>
            )}

            <Divider style={{ margin: "8px 0" }} orientation="left" plain>
              Siguiente estancia
            </Divider>
            {datos.siguiente ? (
              <Estancia estancia={datos.siguiente} onVerVenta={onVerVenta} />
            ) : (
              <div style={{ color: "#6b7280", fontSize: 12 }}>No hay nada reservado después.</div>
            )}

            {/* Lo que viene de una venta programada y todavía no existe como venta. Se lista aparte
                para que no se confunda con un compromiso firme. */}
            <Divider style={{ margin: "8px 0" }} orientation="left" plain>
              Reservas programadas
            </Divider>
            {estanciasProgramadas.length > 0 ? (
              estanciasProgramadas.map((valorEstancia, indice) => (
                <Estancia
                  key={`${valorEstancia.ventaProgramadaId}-${indice}`}
                  estancia={valorEstancia}
                  onVerVentaProgramada={onVerVentaProgramada}
                />
              ))
            ) : (
              <div style={{ color: "#6b7280", fontSize: 12 }}>
                No hay ventas programadas que reserven esta habitación.
              </div>
            )}

            {/* El trabajo pendiente del cuarto. A diferencia de las estancias, esto no se deriva de
                nada: alguien lo anotó. Se ven todas las de la habitación, no solo las de la fecha
                consultada, porque lo que importa es qué le falta al cuarto. */}
            <Divider style={{ margin: "8px 0" }} orientation="left" plain>
              Tareas
            </Divider>

            <ButtonItem
              block
              size="small"
              className="botonAgregar"
              style={{ marginBottom: 8 }}
              onClick={() => setModalTarea({ visible: true, tarea: null })}
            >
              Agregar tarea
            </ButtonItem>

            {tareas.length > 0 ? (
              tareas.map((tarea) => {
                const prioridad =
                  PRIORIDADES.find((valor) => valor.valor === tarea.prioridad) || PRIORIDADES[1];
                return (
                  <div
                    key={tarea._id}
                    style={{
                      border: "1px solid #eef0f6",
                      borderLeft: `3px solid ${
                        tarea.estatus === "pendiente" ? prioridad.color : ESTATUS_TAREA[tarea.estatus].color
                      }`,
                      borderRadius: 8,
                      padding: 10,
                      marginBottom: 8,
                    }}
                  >
                    <div style={{ fontWeight: 700 }}>{tarea.titulo}</div>
                    <div style={{ color: "#6b7280", fontSize: 12, marginTop: 2 }}>
                      {moment(tarea.fechaInicio).format("DD/MM/YYYY")}
                      {tarea.todoElDia === false
                        ? ` · ${moment(tarea.fechaInicio).format("HH:mm")}`
                        : ""}
                      {" · "}
                      {tarea.responsableNombre || "Sin responsable"}
                    </div>
                    <div style={{ marginTop: 6, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                      <Tag color={ESTATUS_TAREA[tarea.estatus]?.color}>
                        {ESTATUS_TAREA[tarea.estatus]?.etiqueta}
                      </Tag>
                      {tarea.estatus === "pendiente" ? (
                        <>
                          <ButtonItem
                            size="small"
                            verde
                            onClick={() => cambiarEstatusTarea(tarea, "terminada")}
                          >
                            Terminar
                          </ButtonItem>
                          <ButtonItem
                            size="small"
                            blanco
                            onClick={() => cambiarEstatusTarea(tarea, "cancelada")}
                          >
                            Cancelar
                          </ButtonItem>
                        </>
                      ) : (
                        <ButtonItem
                          size="small"
                          blanco
                          onClick={() => cambiarEstatusTarea(tarea, "pendiente")}
                        >
                          Reabrir
                        </ButtonItem>
                      )}
                      <ButtonItem
                        size="small"
                        cyanB
                        onClick={() => setModalTarea({ visible: true, tarea })}
                      >
                        Editar
                      </ButtonItem>
                    </div>
                  </div>
                );
              })
            ) : (
              <div style={{ color: "#6b7280", fontSize: 12 }}>
                Esta habitación no tiene tareas registradas.
              </div>
            )}
          </>
        )}
      </Spin>

      <ModalTareaHabitacion
        visible={modalTarea.visible}
        tarea={modalTarea.tarea}
        habitacionFija={habitacionId}
        fechaSugerida={datos?.fecha}
        onCancel={() => setModalTarea({ visible: false, tarea: null })}
        onGuardado={() => {
          setModalTarea({ visible: false, tarea: null });
          cargarTareas();
        }}
      />
    </Drawer>
  );
};

export default HabitacionDetalleDrawer;
